// Which patient and which VISIT DATE a filed report/fax landed on.
//
// The storage path is the record of that decision — the inbound-fax handler
// picks the patient's next visit and moves the file under that visit's folder,
// so the path is authoritative in a way no cache column is:
//
//   New/Reports/{schema}/{Name}_{ehr}/{MM-DD-YY}/{file}   ← filed onto a visit
//   New/Reports/{Name}_{ehr}/{MM-DD-YY}/{file}            ← same, public schema
//   New/Reports/{schema}/{Name}_{ehr}/{file}              ← patient-level copy
//   New/Reports/Pending_Assignment/{file}                 ← not matched yet
//
// Every fax produces BOTH a visit-folder entry and a patient-level roster entry
// (roster_dual_file), and both are cached as faxes, so the inbox lists two rows
// per fax per schema. Only one of them carries a date; the other is deliberately
// date-less because it is filed against the chart as a whole. Callers need to
// tell those apart rather than silently showing nothing for the second.
//
// Parsing walks DIRECTORY segments only and accepts a date folder only when it
// really looks like one. The backend equivalent took "the segment at index 4"
// on faith, which on a patient-level path is the FILE NAME — so an upload like
// `WhatsApp Image 2026-04-24 at 7.34.26 PM.jpeg` split into three dash-parts and
// was reported as a scheduled date.

const REPORTS_ROOT = "New/Reports/";
const DATE_FOLDER = /^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `MM-DD-YY` (or `-YYYY`) storage folder → `YYYY-MM-DD`, or null. */
export function parseDateFolder(segment) {
  const m = DATE_FOLDER.exec(String(segment || "").trim());
  if (!m) return null;
  const [, mm, dd, yy] = m;
  const month = Number(mm);
  const day = Number(dd);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const year = yy.length === 2 ? `20${yy}` : yy;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** `YYYY-MM-DD` → `Jul 31, 2026`. Passes anything unparseable straight back. */
export function formatVisitDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  if (!m) return String(iso || "");
  const [, year, month, day] = m;
  const name = MONTHS[Number(month) - 1];
  if (!name) return iso;
  return `${name} ${Number(day)}, ${year}`;
}

/** Patient folder `Lastname, First_12345` → `{ patientName, ehrId }`. */
export function splitPatientFolder(folder) {
  const text = String(folder || "");
  const cut = text.lastIndexOf("_");
  if (cut === -1) return { patientName: text, ehrId: "" };
  return { patientName: text.slice(0, cut), ehrId: text.slice(cut + 1) };
}

/**
 * Everything the storage path says about a filed report.
 *
 * @returns {{
 *   schema: string,          // "public" when the path carries no schema segment
 *   patientFolder: string,
 *   patientName: string,
 *   ehrId: string,
 *   visitDate: string|null,  // YYYY-MM-DD — the visit it was filed onto
 *   isPatientLevel: boolean, // filed against the chart, not one visit
 *   isUnmatched: boolean,
 *   filename: string,
 * }|null} null when the path is not under New/Reports.
 */
export function parseFaxReportPath(filePath) {
  const path = String(filePath || "").trim();
  if (!path.startsWith(REPORTS_ROOT)) return null;

  const parts = path.slice(REPORTS_ROOT.length).split("/").filter(Boolean);
  if (!parts.length) return null;

  const filename = parts[parts.length - 1];
  const dirs = parts.slice(0, -1);

  const blank = {
    schema: "public",
    patientFolder: "",
    patientName: "",
    ehrId: "",
    visitDate: null,
    isPatientLevel: false,
    isUnmatched: false,
    filename,
  };

  if (!dirs.length) return blank;
  // Tenant pending paths are New/Reports/{schema}/Pending_Assignment/{file}, so
  // this cannot key on the first segment — that read the holding folder as a
  // patient named "Pending_Assignment".
  const pendingAt = dirs.indexOf("Pending_Assignment");
  if (pendingAt !== -1) {
    return {
      ...blank,
      isUnmatched: true,
      schema: pendingAt > 0 ? dirs[0] : "public",
    };
  }

  // Search from the end: only a directory segment shaped like MM-DD-YY counts,
  // which is what keeps a file name from being read as a date.
  let dateIndex = -1;
  for (let i = dirs.length - 1; i >= 0; i -= 1) {
    if (parseDateFolder(dirs[i])) {
      dateIndex = i;
      break;
    }
  }

  const patientIndex = dateIndex > 0 ? dateIndex - 1 : dirs.length - 1;
  const patientFolder = dirs[patientIndex] || "";
  // Anything before the patient folder is the tenant schema; nothing there
  // means a legacy public path.
  const schema = patientIndex > 0 ? dirs[0] : "public";

  return {
    ...blank,
    schema,
    patientFolder,
    ...splitPatientFolder(patientFolder),
    visitDate: dateIndex > 0 ? parseDateFolder(dirs[dateIndex]) : null,
    isPatientLevel: dateIndex === -1,
  };
}

/**
 * The received DOCUMENT a row belongs to, independent of where it was filed.
 *
 * One received fax becomes several inbox rows — the visit folder and the roster
 * folder in its own schema, plus the same pair in every other schema of the
 * clinic group — and every copy keeps the file name it arrived with. The name
 * is therefore the document's identity, which is what lets the inbox say "Filed
 * to: STUDENT, SOC_WORK" and what makes unmatching clear every copy at once
 * (backend `api/fax_unmatch`). Callers acting once per document — rather than
 * once per row — should key on this.
 *
 * @returns {string} the file name, or "" when the row carries no path.
 */
export function faxDocumentKey(fax) {
  const path = typeof fax === "string" ? fax : fax?.file_path;
  return String(path || "").split("/").pop() || "";
}

/**
 * The visit this fax was filed onto, or null.
 *
 * A fax is matched to ONE appointment — the patient's next one, or their most
 * recent past one when nothing is booked. The path of the visit copy says which;
 * the roster copy's path does not, so for that row the answer comes from the
 * API's `scheduled_date`, which the backend resolves from the visit copy of the
 * same document in the same schema.
 *
 * Null therefore means what it should: no appointment was found for this
 * patient — not merely "this row happens to be the roster copy".
 */
export function faxMatchedVisitDate(fax) {
  const fromPath = parseFaxReportPath(fax?.file_path)?.visitDate;
  if (fromPath) return fromPath;
  const fallback = String(fax?.scheduled_date || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(fallback) ? fallback : null;
}

/**
 * The patient name to SHOW for a fax — without the chart number.
 *
 * `fax.patient_name` from the API is the storage folder with trailing
 * underscores trimmed (backend `legacy_patient_label`), so it reads
 * "Li, DIego_123123". The number is part of the folder name, not part of the
 * person's name, and the inbox was printing it as though it were.
 *
 * The path is preferred because it is the authoritative record of where the fax
 * was filed and splits cleanly into name and chart number. The API field is the
 * fallback, split the same way: the folder is built as `{Name}_{ehr}`, so the
 * LAST underscore is the separator by construction — a patient with no chart
 * number has no underscore to split on and comes back untouched.
 *
 * Display only. The API keeps sending the folder-shaped string, which other
 * callers still match on.
 */
export function faxPatientDisplayName(fax) {
  if (!fax) return "";
  const fromPath = parseFaxReportPath(fax.file_path)?.patientName;
  if (fromPath) return fromPath;
  const raw = String(fax.patient_name || "").trim();
  return raw ? splitPatientFolder(raw).patientName || raw : "";
}
